// FILE: ChatHeader.tsx
// Purpose: Renders the chat top bar with project actions and panel toggles.
// Layer: Chat shell header
// Depends on: project action controls, git actions, and panel toggle callbacks

import {
  type EditorId,
  type ProjectId,
  type ProjectScript,
  PROVIDER_DISPLAY_NAMES,
  type ProviderKind,
  type ResolvedKeybindingsConfig,
  type ThreadId,
} from "@taskin-code/contracts";
import { useQuery } from "@tanstack/react-query";
import React, { memo, useEffect, useRef, useState } from "react";
import { BsLayoutSplit, BsTerminal } from "react-icons/bs";
import { FiGitBranch } from "react-icons/fi";
import { HiMiniArrowsPointingOut } from "react-icons/hi2";
import { TbLayoutSidebarRight } from "react-icons/tb";
import { IconFolderSearch } from "@tabler/icons-react";
import GitActionsControl from "../GitActionsControl";
import { ThreadAssigneesControl } from "../ThreadAssigneesControl";
import { AppsIcon, ArrowRightIcon, GlobeIcon, PlusIcon } from "~/lib/icons";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../ui/menu";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import ProjectScriptsControl, { type NewProjectScriptInput } from "../ProjectScriptsControl";
import { Toggle } from "../ui/toggle";
import { SidebarHeaderTrigger, useSidebar } from "../ui/sidebar";
import { isElectron } from "~/env";
import { cn } from "~/lib/utils";
import { readNativeApi } from "~/nativeApi";
import { resolveEditorIcon } from "../../editorMetadata";
import { usePreferredEditor } from "../../editorPreferences";
import { useIsDisposableThread } from "~/hooks/useIsDisposableThread";
import { ClaudeAI, OpenAI } from "../Icons";
import { gitStatusQueryOptions } from "~/lib/gitReactQuery";
import { useProjectUiCapabilities } from "~/auth/projectUiCapabilities";

/** Width (px) below which collapsible header controls fold into the ellipsis menu. */
const HEADER_COMPACT_BREAKPOINT = 480;

interface ChatHeaderProps {
  activeThreadId: ThreadId;
  activeThreadTitle: string;
  activeProjectName: string | undefined;
  activeProjectId: ProjectId | null;
  /**
   * False when the active thread's selected (provider, model) is not in the
   * current user's allowlist. Drives the "Modelo no autorizado" badge.
   */
  isCurrentModelAllowed: boolean;
  /**
   * False when the active thread runs a managed agent the current user may not
   * dispatch — typically a thread shared by a colleague, or one created before
   * an admin narrowed this account's agent allowlist.
   */
  isCurrentAgentAllowed: boolean;
  threadBreadcrumbs: ReadonlyArray<{
    threadId: ThreadId;
    title: string;
  }>;
  hideHandoffControls?: boolean;
  isGitRepo: boolean;
  openInCwd: string | null;
  activeProjectScripts: ProjectScript[] | undefined;
  preferredScriptId: string | null;
  keybindings: ResolvedKeybindingsConfig;
  availableEditors: ReadonlyArray<EditorId>;
  terminalAvailable: boolean;
  terminalOpen: boolean;
  terminalToggleShortcutLabel: string | null;
  browserToggleShortcutLabel: string | null;
  diffToggleShortcutLabel: string | null;
  handoffBadgeLabel: string | null;
  /**
   * Plane issue this thread works, shown as a badge. Null when unlinked.
   * `inherited` means the ref came from an ancestor thread, not this one's own
   * link — the badge renders the same either way, just dimmer, since the
   * agent was told about the same issue regardless of which thread holds it.
   */
  planeIssue?: { ref: string; inherited: boolean } | null;
  /**
   * This thread runs in its author's persistent environment rather than in a
   * worktree of its own. Shown because it is the reason the workspace still has
   * last week's data in it, and because archiving the thread will not take that
   * data with it — both worth knowing before you act.
   */
  /**
   * Label of the persistent environment this thread runs in, or null for an
   * ordinary worktree. A string rather than a boolean because the two kinds do
   * not read alike: "Tu entorno" is your own data, "Entorno del proyecto: qa"
   * is the team's.
   */
  environmentLabel?: string | null;
  /**
   * «Probando PR #131», while this thread's environment is wearing a pull
   * request. Its own badge rather than a suffix on the label above: the
   * environment is a fact about the thread that does not change, and the trial
   * lasts an afternoon.
   */
  environmentTrialLabel?: string | null;
  handoffActionLabel: string;
  handoffDisabled: boolean;
  handoffActionTargetProvider: ProviderKind | null;
  handoffBadgeSourceProvider: ProviderKind | null;
  handoffBadgeTargetProvider: ProviderKind | null;
  browserOpen: boolean;
  gitCwd: string | null;
  diffOpen: boolean;
  diffDisabledReason?: string | null;
  filesOpen: boolean;
  filesDisabled?: boolean;
  filesToggleShortcutLabel?: string | null;
  surfaceMode?: "single" | "split";
  chatLayoutAction?: {
    kind: "split" | "maximize";
    label: string;
    shortcutLabel: string | null;
    onClick: () => void;
  } | null;
  onRunProjectScript: (script: ProjectScript) => void;
  onAddProjectScript: (input: NewProjectScriptInput) => Promise<void>;
  onUpdateProjectScript: (scriptId: string, input: NewProjectScriptInput) => Promise<void>;
  onDeleteProjectScript: (scriptId: string) => Promise<void>;
  onToggleTerminal: () => void;
  onToggleDiff: () => void;
  onToggleBrowser: () => void;
  onToggleFiles: () => void;
  onCreateHandoff: () => void;
  onNavigateToThread: (threadId: ThreadId) => void;
}

export const ChatHeader = memo(function ChatHeader({
  activeThreadId,
  activeThreadTitle,
  activeProjectName,
  activeProjectId,
  isCurrentModelAllowed,
  isCurrentAgentAllowed,
  threadBreadcrumbs,
  hideHandoffControls = false,
  isGitRepo,
  openInCwd,
  activeProjectScripts,
  preferredScriptId,
  keybindings,
  availableEditors,
  terminalAvailable,
  terminalOpen,
  terminalToggleShortcutLabel,
  browserToggleShortcutLabel,
  diffToggleShortcutLabel,
  handoffBadgeLabel,
  planeIssue,
  environmentLabel = null,
  environmentTrialLabel = null,
  handoffActionLabel,
  handoffDisabled,
  handoffActionTargetProvider,
  handoffBadgeSourceProvider,
  handoffBadgeTargetProvider,
  browserOpen,
  gitCwd,
  diffOpen,
  diffDisabledReason = null,
  filesOpen,
  filesDisabled = false,
  filesToggleShortcutLabel = null,
  surfaceMode = "single",
  chatLayoutAction = null,
  onRunProjectScript,
  onAddProjectScript,
  onUpdateProjectScript,
  onDeleteProjectScript,
  onToggleTerminal,
  onToggleDiff,
  onToggleBrowser,
  onToggleFiles,
  onCreateHandoff,
  onNavigateToThread,
}: ChatHeaderProps) {
  const { isMobile, state } = useSidebar();
  const headerRef = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);
  const [openAddActionNonce, setOpenAddActionNonce] = useState(0);
  const [preferredEditor] = usePreferredEditor(availableEditors);
  const EditorIcon = preferredEditor ? resolveEditorIcon(preferredEditor) : null;
  // What this account's grants let the header offer. `canEditFiles` is the same
  // `p.edit` check that used to be inline here, now read from one place with
  // the rest.
  const uiCapabilities = useProjectUiCapabilities(activeProjectId);
  const canEditActiveProject = uiCapabilities.canEditFiles;
  // Reuse the shared git status query so the diff toggle can show live totals
  // without introducing a second API shape just for the header control.
  const { data: gitStatus = null } = useQuery(gitStatusQueryOptions(gitCwd));
  const diffTotals = gitStatus?.workingTree ?? null;
  const showDiffTotals = (diffTotals?.insertions ?? 0) > 0 || (diffTotals?.deletions ?? 0) > 0;
  const isDisposableThread = useIsDisposableThread(activeThreadId);
  const projectLabel = isDisposableThread ? null : (activeProjectName ?? null);

  const isSplitPane = surfaceMode === "split";

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setCompact(isSplitPane || el.clientWidth < HEADER_COMPACT_BREAKPOINT);
    measure();
    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, [isSplitPane]);

  const renderProviderIcon = (provider: ProviderKind | null, className: string) => {
    if (provider === "claudeAgent") {
      return <ClaudeAI className={cn("text-foreground", className)} />;
    }
    if (provider === "codex") {
      return <OpenAI className={cn("text-muted-foreground/75", className)} />;
    }
    return <FiGitBranch className={className} />;
  };

  return (
    <div
      ref={headerRef}
      className={cn(
        "scrollbar-none flex min-w-0 flex-1 items-center overflow-x-auto",
        !isMobile && state === "collapsed" ? "gap-4" : "gap-2 sm:gap-3",
      )}
    >
      {/*
       * The trigger sits outside the clipping container on purpose. Inside it,
       * a narrow viewport lets the action buttons squeeze this row down to a
       * few pixels and `overflow-hidden` slices the trigger in half — leaving
       * the phone with no way to reach the sidebar.
       */}
      <SidebarHeaderTrigger className="size-7 shrink-0" />
      <div className="flex min-w-0 flex-1 items-center overflow-hidden">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <div className="flex min-w-0 flex-1 flex-col">
            {/*
             * Project first, then the chain of parent threads, on the one line
             * above the title. A thread name alone says nothing about where the
             * work is happening, and that is the question you have on a phone,
             * where the sidebar that used to answer it is collapsed out of view.
             * Disposable threads are left out: they belong to no project, so the
             * same guard as the rest of the project-scoped controls applies.
             */}
            {projectLabel || threadBreadcrumbs.length > 0 ? (
              <div className="flex min-w-0 items-center gap-1 overflow-hidden text-[11px] text-muted-foreground/55">
                {projectLabel ? (
                  // Brand blue held back: enough colour to place the project at a
                  // glance, not enough to compete with the messages, which are
                  // what the eye should land on. Dimmer on dark, where the brand
                  // token is a light blue that carries fine at 55%; the light
                  // theme's darker blue needs more of itself to stay readable at
                  // 11px against a white background.
                  <span
                    className="min-w-0 truncate text-brand-foreground/75 dark:text-brand-foreground/55"
                    title={projectLabel}
                  >
                    {projectLabel}
                  </span>
                ) : null}
                {threadBreadcrumbs.map((breadcrumb, index) => (
                  <React.Fragment key={breadcrumb.threadId}>
                    {index > 0 || projectLabel ? (
                      <span className="shrink-0 text-muted-foreground/35">/</span>
                    ) : null}
                    <button
                      type="button"
                      className="min-w-0 truncate transition-colors hover:text-foreground/80"
                      title={breadcrumb.title}
                      onClick={() => onNavigateToThread(breadcrumb.threadId)}
                    >
                      {breadcrumb.title}
                    </button>
                  </React.Fragment>
                ))}
              </div>
            ) : null}
            <div className="flex min-w-0 items-center gap-2">
              <h2
                className="max-w-[clamp(16rem,50vw,40rem)] truncate text-sm font-medium text-foreground"
                title={activeThreadTitle}
              >
                {activeThreadTitle}
              </h2>
              {!isCurrentModelAllowed ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className="!h-6 shrink-0 gap-1 rounded-md border-amber-500/50 bg-amber-500/10 px-1.5 text-[10px] text-amber-300"
                      >
                        Modelo no autorizado
                      </Badge>
                    }
                  />
                  <TooltipPopup side="bottom" className="max-w-72">
                    El modelo de este thread no está en tu allowlist. Selecciona un modelo permitido
                    en el picker antes de enviar.
                  </TooltipPopup>
                </Tooltip>
              ) : null}
              {!isCurrentAgentAllowed ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className="!h-6 shrink-0 gap-1 rounded-md border-amber-500/50 bg-amber-500/10 px-1.5 text-[10px] text-amber-300"
                      >
                        Agente no autorizado
                      </Badge>
                    }
                  />
                  <TooltipPopup side="bottom" className="max-w-72">
                    El agente de este thread no está entre los que tienes asignados. Selecciona uno
                    permitido en el picker antes de enviar.
                  </TooltipPopup>
                </Tooltip>
              ) : null}
              {environmentLabel !== null ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className="!h-6 shrink-0 gap-1 rounded-md border-emerald-500/40 bg-emerald-500/10 px-1.5 text-[10px] text-emerald-300"
                      >
                        {environmentLabel}
                      </Badge>
                    }
                  />
                  <TooltipPopup side="bottom" className="max-w-72">
                    Este chat trabaja en un entorno permanente: la misma copia del código, la misma
                    preview y las mismas bases de datos de prueba que los demás chats del entorno.
                    Archivarlo no borra nada de eso.
                  </TooltipPopup>
                </Tooltip>
              ) : null}
              {environmentTrialLabel !== null ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className="!h-6 shrink-0 gap-1 rounded-md border-sky-500/40 bg-sky-500/10 px-1.5 text-[10px] text-sky-300"
                      >
                        {environmentTrialLabel}
                      </Badge>
                    }
                  />
                  <TooltipPopup side="bottom" className="max-w-72">
                    El entorno de este chat tiene ese pull request montado encima de su rama base:
                    lo que ves en su preview es ese PR, no la rama base. Hasta que se vuelva a la
                    base, no se puede actualizar el entorno.
                  </TooltipPopup>
                </Tooltip>
              ) : null}
              {planeIssue ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className={cn(
                          "!h-6 shrink-0 gap-1 rounded-md px-1.5 font-mono text-[10px]",
                          planeIssue.inherited && "opacity-60",
                        )}
                      >
                        {planeIssue.ref}
                      </Badge>
                    }
                  />
                  <TooltipPopup side="bottom" className="max-w-72">
                    {planeIssue.inherited
                      ? `Heredado del hilo padre: este hilo trabaja el issue ${planeIssue.ref} de Plane. Los agentes lo leen y comentan sin que se lo repitas.`
                      : `Este hilo está vinculado al issue ${planeIssue.ref} de Plane. Los agentes lo leen y comentan sin que se lo repitas.`}
                  </TooltipPopup>
                </Tooltip>
              ) : null}
              {!hideHandoffControls && handoffBadgeLabel ? (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Badge
                        variant="outline"
                        className="hidden !h-6 shrink-0 items-center justify-center gap-1 rounded-md px-1.5 text-[10px] sm:inline-flex"
                      >
                        <span className="inline-flex size-4 shrink-0 items-center justify-center">
                          {renderProviderIcon(handoffBadgeSourceProvider, "size-3")}
                        </span>
                        <ArrowRightIcon className="size-2.5 shrink-0 opacity-45" />
                        <span className="inline-flex size-4 shrink-0 items-center justify-center">
                          {renderProviderIcon(handoffBadgeTargetProvider, "size-3")}
                        </span>
                      </Badge>
                    }
                  />
                  <TooltipPopup side="bottom">{handoffBadgeLabel}</TooltipPopup>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 [-webkit-app-region:no-drag]">
        {/*
         * Three controls step aside on a phone so the thread title has room to
         * exist at all: provider handoff, the git actions button and the panel
         * menu. `sm:contents` keeps them as direct flex children above the
         * breakpoint, so the desktop row is byte-for-byte what it was.
         */}
        {!isDisposableThread && !hideHandoffControls && uiCapabilities.canHandOffThread ? (
          <div className="hidden sm:contents">
            <Tooltip>
              <TooltipTrigger
                render={
                  <Button
                    type="button"
                    size="xs"
                    variant="outline"
                    className={compact ? "shrink-0 gap-1" : "shrink-0 gap-1.5"}
                    aria-label={handoffActionLabel}
                    disabled={handoffDisabled}
                    onClick={onCreateHandoff}
                  >
                    <FiGitBranch className="size-3.5 shrink-0" />
                    {compact ? (
                      <ArrowRightIcon className="size-2.5 shrink-0 opacity-45" />
                    ) : (
                      <span className="truncate font-normal">Hand off to</span>
                    )}
                    {renderProviderIcon(handoffActionTargetProvider, "size-3.5 shrink-0")}
                    {!compact && (
                      <span className="truncate font-normal">
                        {PROVIDER_DISPLAY_NAMES[handoffActionTargetProvider ?? "codex"]}
                      </span>
                    )}
                  </Button>
                }
              />
              <TooltipPopup side="bottom">{handoffActionLabel}</TooltipPopup>
            </Tooltip>
          </div>
        ) : null}
        {/* Keep one shared project-actions controller mounted so both inline and
            compact header menus open the same dialog/state machine. */}
        {!isDisposableThread && activeProjectScripts ? (
          <ProjectScriptsControl
            scripts={activeProjectScripts}
            keybindings={keybindings}
            preferredScriptId={preferredScriptId}
            showInlineControls={!compact}
            openAddActionNonce={openAddActionNonce}
            canRun={uiCapabilities.canRunActions}
            canCreate={uiCapabilities.canCreateActions}
            onRunScript={onRunProjectScript}
            onAddScript={onAddProjectScript}
            onUpdateScript={onUpdateProjectScript}
            onDeleteScript={onDeleteProjectScript}
          />
        ) : null}

        {/* Panel toggles menu — editor, terminal, browser, split chat. */}
        {!isDisposableThread &&
        (terminalAvailable || activeProjectName || chatLayoutAction || isElectron) ? (
          <div className="hidden sm:contents">
            <Menu modal={false}>
              <MenuTrigger
                render={
                  <Button
                    size="icon-xs"
                    variant="outline"
                    className="shrink-0"
                    aria-label="Panel toggles"
                  />
                }
              >
                <AppsIcon className="size-3.5" />
              </MenuTrigger>
              <MenuPopup
                align="end"
                side="bottom"
                className="w-50 rounded-lg border-border bg-popover shadow-lg"
              >
                {activeProjectName && canEditActiveProject ? (
                  <MenuItem
                    onClick={() => {
                      const api = readNativeApi();
                      if (api && openInCwd && preferredEditor) {
                        void api.shell.openInEditor(openInCwd, preferredEditor);
                      }
                    }}
                    disabled={!preferredEditor || !openInCwd}
                  >
                    {EditorIcon ? (
                      <EditorIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    ) : null}
                    <span>Open in editor</span>
                  </MenuItem>
                ) : null}
                {uiCapabilities.canUseTerminal ? (
                  <MenuItem onClick={onToggleTerminal} disabled={!terminalAvailable}>
                    <BsTerminal className="size-3.5 shrink-0" />
                    <span>{terminalOpen ? "Hide terminal" : "Show terminal"}</span>
                    {terminalToggleShortcutLabel && (
                      <span className="ml-auto text-[11px] opacity-60">
                        {terminalToggleShortcutLabel}
                      </span>
                    )}
                  </MenuItem>
                ) : null}
                {isElectron ? (
                  <MenuItem onClick={onToggleBrowser}>
                    <GlobeIcon className="size-3.5 shrink-0" />
                    <span>{browserOpen ? "Hide browser" : "Show browser"}</span>
                    {browserToggleShortcutLabel && (
                      <span className="ml-auto text-[11px] opacity-60">
                        {browserToggleShortcutLabel}
                      </span>
                    )}
                  </MenuItem>
                ) : null}
                {chatLayoutAction ? (
                  <MenuItem onClick={chatLayoutAction.onClick}>
                    {chatLayoutAction.kind === "split" ? (
                      <BsLayoutSplit className="size-3.5 shrink-0" />
                    ) : (
                      <HiMiniArrowsPointingOut className="size-3.5 shrink-0" />
                    )}
                    <span>{chatLayoutAction.label}</span>
                    {chatLayoutAction.shortcutLabel && (
                      <span className="ml-auto text-[11px] opacity-60">
                        {chatLayoutAction.shortcutLabel}
                      </span>
                    )}
                  </MenuItem>
                ) : null}
                {activeProjectScripts && uiCapabilities.canCreateActions ? (
                  <>
                    <MenuSeparator className="mx-1" />
                    <MenuItem onClick={() => setOpenAddActionNonce((current) => current + 1)}>
                      <PlusIcon className="size-3.5 shrink-0" />
                      <span>Add action</span>
                    </MenuItem>
                  </>
                ) : null}
              </MenuPopup>
            </Menu>
          </div>
        ) : null}

        {!isDisposableThread ? <ThreadAssigneesControl threadId={activeThreadId} /> : null}
        {!isDisposableThread && activeProjectName && uiCapabilities.canWriteGit ? (
          <div className="hidden sm:contents">
            <GitActionsControl gitCwd={gitCwd} activeThreadId={activeThreadId} />
          </div>
        ) : null}
        {uiCapabilities.canViewFiles ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Toggle
                  className="shrink-0 border-0"
                  pressed={filesOpen}
                  onPressedChange={onToggleFiles}
                  aria-label="Toggle files panel"
                  variant="default"
                  size="xs"
                  disabled={filesDisabled && !filesOpen}
                >
                  <IconFolderSearch className="size-3.5" />
                </Toggle>
              }
            />
            <TooltipPopup side="bottom">
              {filesDisabled && !filesOpen
                ? "Files panel is unavailable for this thread."
                : filesToggleShortcutLabel
                  ? `Toggle files panel (${filesToggleShortcutLabel})`
                  : "Toggle files panel"}
            </TooltipPopup>
          </Tooltip>
        ) : null}
        {uiCapabilities.canViewDiff ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <Toggle
                  className={cn(
                    "shrink-0 border-0",
                    showDiffTotals
                      ? "gap-2 px-1.5 text-[length:var(--app-font-size-ui-sm,11px)]"
                      : "",
                  )}
                  pressed={diffOpen}
                  onPressedChange={onToggleDiff}
                  aria-label="Toggle diff panel"
                  variant="default"
                  size="xs"
                  disabled={!isGitRepo || (diffDisabledReason !== null && !diffOpen)}
                >
                  {showDiffTotals ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="font-chat-code text-[length:var(--app-font-size-ui-sm,11px)] sm:text-[length:var(--app-font-size-ui-xs,10px)] font-normal tracking-normal tabular-nums text-success">
                        +{diffTotals?.insertions ?? 0}
                      </span>
                      <span className="font-chat-code text-[length:var(--app-font-size-ui-sm,11px)] sm:text-[length:var(--app-font-size-ui-xs,10px)] font-normal tracking-normal tabular-nums text-destructive">
                        -{diffTotals?.deletions ?? 0}
                      </span>
                    </span>
                  ) : null}
                  <TbLayoutSidebarRight className="size-3.5" />
                </Toggle>
              }
            />
            <TooltipPopup side="bottom">
              {!isGitRepo
                ? "Diff panel is unavailable because this project is not a git repository."
                : diffDisabledReason && !diffOpen
                  ? diffDisabledReason
                  : diffToggleShortcutLabel
                    ? `Toggle diff panel (${diffToggleShortcutLabel})`
                    : "Toggle diff panel"}
            </TooltipPopup>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
});
